/**
 * CalculationTab — veřejný vstup záložky „Calculation“ vloženy do detailu seriálu i termínu. Tenký LOADER:
 * načte kalkulační data (série/termín) a po dostupnosti předá {@link CalculationContent}, který drží editovatelný
 * stav tarifů a výpočet. Loading/error stavy se řeší zde, aby se obsah nikdy nevykreslil bez dat.
 */
import { useTranslation } from 'react-i18next';

import type { SerieLayerStateItem } from '@/api/generated/schemas';
import {
  useGetVenuaPlannerSerieCalculation,
  useGetVenuaPlannerTermCalculation,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { DETAIL_QUERY_STALE_TIME_MS } from '@/shared/lib/queryClient';
import { Alert } from '@/shared/ui';
import { CalculationContent } from './CalculationContent';

/** Rozbalený (a tím vybraný) termín — řídí ho URL stav detailu, aby ho obnovil reload i deep-link. */
interface ExpandedTermControl {
  expandedTermId: string | null;
  onExpandedTermIdChange: (termId: string | null) => void;
}

type CalculationTabProps = ExpandedTermControl &
  (
    | {
        scope: 'serie';
        productId: string;
        serieId: string;
        termId?: undefined;
        /** Vrstvy seriálu – schvalovací tlačítko v patičce boardu. */
        layers: SerieLayerStateItem[];
        serieLayers?: undefined;
        onBack: () => void;
      }
    | {
        scope: 'term';
        productId: string;
        serieId: string;
        termId: string;
        /** Vrstvy termínu. */
        layers: SerieLayerStateItem[];
        /** Vrstvy seriálu – zdroj nabídky „schválit až po X“. */
        serieLayers: SerieLayerStateItem[];
        onBack: () => void;
      }
  );

export function CalculationTab(props: CalculationTabProps) {
  const { t } = useTranslation('venua-planner');

  const serieQuery = useGetVenuaPlannerSerieCalculation(props.productId, props.serieId, {
    query: { staleTime: DETAIL_QUERY_STALE_TIME_MS, enabled: props.scope === 'serie' },
  });
  const termQuery = useGetVenuaPlannerTermCalculation(
    props.productId,
    props.serieId,
    props.scope === 'term' ? props.termId : '',
    { query: { staleTime: DETAIL_QUERY_STALE_TIME_MS, enabled: props.scope === 'term' } }
  );

  const query = props.scope === 'serie' ? serieQuery : termQuery;

  if (query.isLoading) {
    return (
      <div className="bg-background flex h-full items-center justify-center">
        <p className="text-phx-text-muted text-phx-sm">{t('calculation.loading')}</p>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="bg-background p-phx-5 h-full">
        <Alert severity="error" title={t('calculation.loadErrorTitle')}>
          {t('calculation.loadError')}
        </Alert>
      </div>
    );
  }

  return <CalculationContent {...props} response={query.data} />;
}
